import { useEffect, useState } from "react";
import { getLtik, getLtiKeyFromStorages, LTIK_LS_KEY, postGrade } from "../api";
import { jwtDecode, type JwtPayload } from "jwt-decode";
import { useAppSelector } from "../app/store";
import { COMPLETED_CONTROL_SURVEY_ROUTE } from "../shared/routePaths";

const LTIK_SESSION_DATA_LS_KEY = "ltikSessionData";

interface LtikJwtPayload extends JwtPayload {
  user?: string;
}

export interface LtikSessionData {
  graded: boolean;
}

type LtikSessionDataObject = Record<string, LtikSessionData>;

export function useWithLtikSession() {
  const [submittingGrade, setSubmittingGrade] = useState(false);
  const [gradePosted, setGradePosted] = useState(false);

  const user = useAppSelector((state) => state.user.data);
  const completedPostSurvey = user?.status.isPostTestSurveyComplete ?? false;

  function getUserIdFromLtikToken(token: string): string | null {
    const decoded = jwtDecode<LtikJwtPayload>(token);
    return typeof decoded.user === "string" ? decoded.user : null;
  }

  function updateAllLtikSessionData(data: LtikSessionDataObject) {
    localStorage.setItem(LTIK_SESSION_DATA_LS_KEY, JSON.stringify(data));
  }

  function getCurrentLtikSessionData(): LtikSessionData | null {
    const ltik = getLtiKeyFromStorages();
    if (!ltik) return null;

    try {
      const userId = getUserIdFromLtikToken(ltik);
      if (!userId) return null;

      const ltik_in_storage = localStorage.getItem(LTIK_SESSION_DATA_LS_KEY);
      const allLtikSessionData: LtikSessionDataObject = ltik_in_storage
        ? (JSON.parse(ltik_in_storage) as LtikSessionDataObject)
        : {};

      return allLtikSessionData[userId] ?? null;
    } catch (error) {
      console.error("Error decoding ltik:", error);
      return null;
    }
  }

  function getCanvasIdFromLtikToken(): string {
    try {
      const token = getLtik();
      return getUserIdFromLtikToken(token) ?? "";
    } catch (error) {
      console.error("Error decoding ltik token:", error);
      return "";
    }
  }

  useEffect(() => {
    const ltik = getLtiKeyFromStorages();
    if (!ltik) return;
    localStorage.setItem(LTIK_LS_KEY, ltik);

    try {
      const userId = getUserIdFromLtikToken(ltik);
      if (!userId) return;

      const isAtCompletedControlSurveyRoute = window.location.pathname.includes(
        COMPLETED_CONTROL_SURVEY_ROUTE,
      );
      const ltik_in_storage = localStorage.getItem(LTIK_SESSION_DATA_LS_KEY);
      const allLtikSessionData: LtikSessionDataObject = ltik_in_storage
        ? (JSON.parse(ltik_in_storage) as LtikSessionDataObject)
        : {};

      allLtikSessionData[userId] = {
        graded: false,
      };

      if (
        (completedPostSurvey && !allLtikSessionData[userId].graded) ||
        isAtCompletedControlSurveyRoute
      ) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSubmittingGrade(true);

        postGrade(100)
          .catch((error: unknown) => {
            console.error("Error posting grade:", error);
          })
          .finally(() => {
            allLtikSessionData[userId] = { graded: true };
            updateAllLtikSessionData(allLtikSessionData);
            setGradePosted(true);
            setSubmittingGrade(false);
          });
      } else {
        updateAllLtikSessionData(allLtikSessionData);
      }
    } catch (error) {
      console.error("Error decoding ltik:", error);
    }
  }, [completedPostSurvey]);

  return {
    gradePosted,
    submittingGrade,
    isLtikSession: getCurrentLtikSessionData() ? true : false,
    canvasId: getCanvasIdFromLtikToken(),
  };
}
